import {
  RecommendationAction,
  RecommendationActionEntry,
  RecommendationActionsSnapshot,
} from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';

export interface LocalActionCounts {
  acceptedCount: number;
  actionedCount: number;
  overriddenCount: number;
  rejectedCount: number;
}

/**
 * The decision an entry carries, without the bookkeeping around it. Comparing
 * signatures tells whether an entry still holds the decision it was last
 * persisted with, or has been edited since.
 */
export const serializeActionEntry = (
  entry: RecommendationActionEntry,
): string =>
  JSON.stringify({
    action: entry.action,
    overrideGrossProfitInPercentage:
      entry.overrideGrossProfitInPercentage ?? null,
    overridePriceInMicros: entry.overridePriceInMicros ?? null,
    rejectionReason: entry.rejectionReason ?? null,
  });

/** Whether the entry's decision is already persisted on the backend. */
export const isActionEntrySynced = (
  entry: RecommendationActionEntry,
): boolean =>
  entry.syncedSignature !== undefined &&
  entry.syncedSignature === serializeActionEntry(entry);

const countEntries = (
  entries: RecommendationActionEntry[],
): LocalActionCounts => {
  let accepted = 0;
  let rejected = 0;
  let overridden = 0;
  for (const entry of entries) {
    if (entry.action === RecommendationAction.Rejected) {
      rejected += 1;
    } else if (entry.action === RecommendationAction.Overridden) {
      overridden += 1;
    } else if (entry.action === RecommendationAction.Accepted) {
      accepted += 1;
    }
  }
  return {
    acceptedCount: accepted,
    actionedCount: accepted + rejected + overridden,
    overriddenCount: overridden,
    rejectedCount: rejected,
  };
};

export const reduceActionCounts = (
  actions: RecommendationActionsSnapshot,
): LocalActionCounts => countEntries(Object.values(actions));

/**
 * Counts only the decisions the backend has not stored yet. Progress displays
 * add these on top of a published baseline, so a decision already saved by the
 * status sync would otherwise be counted twice once the baseline catches up.
 */
export const reduceUnsyncedActionCounts = (
  actions: RecommendationActionsSnapshot,
): LocalActionCounts =>
  countEntries(
    Object.values(actions).filter((entry) => !isActionEntrySynced(entry)),
  );
